import { useNow } from "../api/jobs";
import { useToggleWatcher, useTranscriptionWatcher } from "../api/queries";
import type { TranscriptionWatcher } from "../api/types";
import { Card, ErrorBanner, Loading, PageHeader, RelativeTime } from "../components/ui";
import { absoluteTime } from "../lib/format";

// Admin settings. Currently just the transcription watcher (moved here from the
// Transcriptions page) — the scheduled job that pulls new Moodle media and
// transcribes it automatically. It's controlled by a single on/off toggle so it
// can be started and stopped without leaving the app.
export function SettingsPage() {
  const watcher = useTranscriptionWatcher();

  return (
    <div className="p-6">
      <PageHeader
        title="Settings"
        subtitle="Automatic jobs and background services for this admin."
      />

      <div>
        {watcher.isLoading ? (
          <Card><Loading /></Card>
        ) : (
          <WatcherSettings watcher={watcher.data} error={watcher.error} />
        )}
      </div>
    </div>
  );
}

function WatcherSettings({ watcher, error }: { watcher: TranscriptionWatcher | undefined; error: unknown }) {
  const toggle = useToggleWatcher();
  if (error) return <ErrorBanner error={error} />;
  if (!watcher) return null;

  // `enabled` is the server-side feature flag (TRANSCRIPTION_ENABLED); `paused`
  // is the runtime on/off state the toggle drives.
  const on = !watcher.paused;
  const run = watcher.last_run;

  return (
    <Card title="Transcription watcher">
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {watcher.enabled
              ? on
                ? "Watching for new media"
                : "Stopped"
              : "Unavailable"}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {watcher.enabled ? (
              <>
                Automatically pulls new Moodle audio/video and transcribes it every{" "}
                {watcher.interval_minutes} min.
              </>
            ) : (
              <>Automatic transcription is disabled on the server (TRANSCRIPTION_ENABLED). Files are only transcribed when you trigger them from the Transcriptions page.</>
            )}
          </p>
        </div>

        <Toggle
          checked={watcher.enabled && on}
          disabled={!watcher.enabled || toggle.isPending}
          onChange={(next) => toggle.mutate(!next)}
          label="Transcription watcher"
        />
      </div>

      {watcher.enabled && (
        <dl className="mt-5 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Status</dt>
            <dd className="mt-1 font-medium text-gray-900">{on ? "Running" : "Paused"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Next check</dt>
            <dd className="mt-1 text-gray-700">
              {on
                ? watcher.next_check_at
                  ? <Until iso={watcher.next_check_at} />
                  : "after the first scheduled run"
                : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">Last run</dt>
            <dd className="mt-1 text-gray-700">
              {!run ? (
                <span className="text-gray-500">none recorded yet</span>
              ) : run.error ? (
                <span className="text-red-700">failed — {run.error}</span>
              ) : (
                <span>
                  {run.completed ?? 0} of {run.assets_seen ?? 0} transcribed{run.failed ? `, ${run.failed} failed` : ""}
                  <span className="text-gray-500"> (<RelativeTime iso={run.finished_at ?? run.started_at} />)</span>
                </span>
              )}
            </dd>
          </div>
        </dl>
      )}

      <ErrorBanner error={toggle.error} />
    </Card>
  );
}

// A small accessible on/off switch.
function Toggle({ checked, disabled, onChange, label }: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-brand" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

// Countdown to an (estimated) future time; "due now" once it has passed.
function Until({ iso }: { iso: string }) {
  const now = useNow(true);
  const mins = Math.round((new Date(iso).getTime() - now) / 60_000);
  const text = mins <= 0 ? "due now" : mins < 60 ? `in ${mins} min` : `in ${Math.floor(mins / 60)}h ${mins % 60}m`;
  return <time dateTime={iso} title={absoluteTime(iso)}>{text}</time>;
}
